import { useState } from 'react';
import type { ReactNode } from 'react';
import { ConfirmDialog } from './confirm-dialog';
import { Input } from './input';

interface TypeToConfirmDialogProps {
  isOpen: boolean;
  title: string;
  // Body content above the type-to-confirm input — the caller renders the
  // human-readable summary of what's about to be deleted (counts, names,
  // etc.). The input is appended below this block.
  description: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  variant?: 'danger' | 'primary' | 'secondary';
  // Value the user must type EXACTLY to enable the confirm button.
  // Match is trimmed but case-sensitive — for irreversible deletes
  // ("Aerial Apartments" vs "aerial apartments"), the friction of
  // typing the name correctly is the whole point. When empty/
  // whitespace, the dialog degrades to a plain ConfirmDialog.
  matchValue: string;
  // Label for the input prompting the user. e.g., "property name".
  matchLabel?: string;
}

// Destructive-confirmation dialog with a name-typing guard. Use in place
// of ConfirmDialog for operations that are irreversible enough that an
// extra friction step is worth the safety: deleting a property and all
// its devices/units, dropping a tenant, etc.
//
// Match is exact (case-sensitive), trimmed only at the leading/trailing
// edges. The friction of typing the property name correctly — caps and
// all — is the entire point: clicking Confirm on autopilot is impossible.
export function TypeToConfirmDialog({
  isOpen,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel,
  onConfirm,
  onCancel,
  isLoading,
  variant = 'danger',
  matchValue,
  matchLabel = 'name',
}: TypeToConfirmDialogProps) {
  // Bail before mounting the input so each open is a fresh mount with an
  // empty typed value — avoids the typed text leaking across opens
  // without needing an effect to reset it.
  if (!isOpen) return null;
  return (
    <TypeToConfirmDialogInner
      title={title}
      description={description}
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
      onConfirm={onConfirm}
      onCancel={onCancel}
      isLoading={isLoading}
      variant={variant}
      matchValue={matchValue}
      matchLabel={matchLabel}
    />
  );
}

function TypeToConfirmDialogInner({
  title,
  description,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  isLoading,
  variant,
  matchValue,
  matchLabel,
}: Omit<TypeToConfirmDialogProps, 'isOpen'>) {
  const [typed, setTyped] = useState('');

  const target = matchValue.trim();
  const matched = target.length > 0 && typed.trim() === target;
  // When matchValue is empty/whitespace (shouldn't happen for a sensibly
  // named property, but defends against bad data), fall through to a
  // plain confirmation rather than locking the user out.
  const guardActive = target.length > 0;
  const disabled = guardActive && !matched;

  const composedDescription: ReactNode = (
    <>
      {description}
      {guardActive && (
        <Input
          label={`Type the ${matchLabel} to confirm`}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={matchValue}
          autoFocus
          disabled={isLoading}
        />
      )}
    </>
  );

  return (
    <ConfirmDialog
      isOpen
      title={title}
      description={composedDescription}
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
      onConfirm={onConfirm}
      onCancel={onCancel}
      isLoading={isLoading}
      disabled={disabled}
      variant={variant}
    />
  );
}
